import React, { useEffect, useState } from "react";
import { MessageSquarePlus, X, Send, Paperclip, Trash2, Check } from "lucide-react";
import { api, formatError } from "../../lib/api";

const CATEGORY_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "feature_request", label: "Feature request" },
  { value: "content", label: "Content" },
  { value: "usability", label: "Usability" },
  { value: "billing", label: "Billing" },
  { value: "other", label: "Other" },
];

const ACCEPTED_MIME = ["image/png", "image/jpeg", "image/webp"];
const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Floating feedback launcher pinned bottom-left so it never collides with
 * the AssistantWidget (which sits at bottom-right).
 */
export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [consentContact, setConsentContact] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    // Reset the form when the popup closes so a re-open starts clean.
    if (!open) {
      setError(null);
      setSuccessId(null);
    }
  }, [open]);

  // Sidebar "Share Your Feedback" button dispatches this event to open
  // the popup. Kept as a global window event so any surface (topbar,
  // help menu, empty states, etc.) can trigger the same widget without
  // prop-drilling. Toggle-on only — closing is the user's job via X.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("cpg:open-feedback", handler);
    return () => window.removeEventListener("cpg:open-feedback", handler);
  }, []);

  const addFiles = (list) => {
    const arr = Array.from(list);
    const problems = [];
    const accepted = [];
    for (const f of arr) {
      if (accepted.length + files.length >= MAX_FILES) {
        problems.push(`Only ${MAX_FILES} screenshots per report.`);
        break;
      }
      if (!ACCEPTED_MIME.includes(f.type)) {
        problems.push(`${f.name}: only PNG, JPEG, or WebP.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        problems.push(`${f.name}: exceeds 5 MB.`);
        continue;
      }
      accepted.push(f);
    }
    if (problems.length) setError(problems.join(" "));
    else setError(null);
    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (evt) => {
    evt.preventDefault();
    setError(null);
    setSuccessId(null);
    if (title.trim().length < 3) return setError("Title must be at least 3 characters.");
    if (body.trim().length < 3) return setError("Description must be at least 3 characters.");
    setSubmitting(true);
    try {
      const r = await api.post("/feedback", {
        category, title: title.trim(), body: body.trim(),
        page_url: typeof window !== "undefined" ? window.location.pathname : null,
        consent_to_contact: consentContact,
      });
      const feedbackId = r.data.id;
      // Upload attachments sequentially — small count so serial is fine.
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f, f.name);
        await api.post(`/feedback/${feedbackId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setSuccessId(feedbackId);
      setTitle(""); setBody(""); setFiles([]); setConsentContact(false);
      setCategory("bug");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher removed per agentic edit — the "Share Your
          Feedback" button in the sidebar (AppShell) now dispatches a
          `cpg:open-feedback` window event that flips this widget open.
          A close (X) button lives inside the panel below. */}

      {open && (
        <div
          data-testid="feedback-panel"
          role="dialog"
          aria-label="Send feedback"
          className="fixed z-40 bottom-20 left-3 md:left-6 w-[calc(100vw-1.5rem)] md:w-[440px] max-w-[480px] max-h-[85vh] bg-white rounded-2xl border border-neutral-200 shadow-2xl overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Send feedback</div>
              <div className="text-[11px] text-neutral-500">
                We read every submission. Include a screenshot if it helps.
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="p-1 rounded-md hover:bg-neutral-100"
            ><X className="h-4 w-4" /></button>
          </div>

          <form onSubmit={submit} className="overflow-y-auto px-4 py-3 space-y-3">
            {successId ? (
              <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2" data-testid="feedback-success">
                <Check className="h-4 w-4 mt-[2px]" />
                <div>
                  <div className="font-semibold">Thanks — we got it.</div>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    Reference: <code className="font-mono">{successId.slice(0, 8)}</code>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Category</label>
                  <select
                    data-testid="feedback-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Title</label>
                  <input
                    data-testid="feedback-title"
                    type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                    placeholder="Short summary"
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Description</label>
                  <textarea
                    data-testid="feedback-body"
                    value={body} onChange={(e) => setBody(e.target.value)}
                    rows={4} maxLength={8000}
                    placeholder="What happened? What did you expect?"
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 flex items-center justify-between">
                    <span>Screenshots (optional)</span>
                    <span className="text-[10px] text-neutral-500 font-normal">
                      Up to {MAX_FILES} · 5 MB each · PNG/JPG/WebP
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-neutral-700 bg-neutral-50 border border-dashed border-neutral-300 rounded-md px-3 py-2 cursor-pointer hover:bg-neutral-100">
                    <Paperclip className="h-4 w-4" />
                    <span>Add screenshot</span>
                    <input
                      data-testid="feedback-file-input"
                      type="file" multiple accept={ACCEPTED_MIME.join(",")}
                      className="hidden"
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    />
                  </label>
                  {files.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {files.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1">
                          <span className="truncate max-w-[70%]" title={f.name}>{f.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-neutral-500">{Math.round(f.size / 1024)} KB</span>
                            <button type="button" onClick={() => removeFile(i)} className="text-neutral-500 hover:text-red-600" aria-label="Remove">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <label className="flex items-start gap-2 text-xs text-neutral-700">
                  <input
                    data-testid="feedback-consent-contact"
                    type="checkbox" checked={consentContact}
                    onChange={(e) => setConsentContact(e.target.checked)}
                    className="mt-[3px]"
                  />
                  <span>OK to contact me about this feedback (optional).</span>
                </label>
                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="feedback-error">{error}</div>
                )}
              </>
            )}
          </form>

          {!successId && (
            <div className="border-t border-neutral-200 px-4 py-2 flex justify-end">
              <button
                type="button"
                data-testid="feedback-submit-btn"
                disabled={submitting}
                onClick={submit}
                className="inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm px-3 py-1.5 disabled:opacity-40 hover:bg-neutral-800"
              >
                <Send className="h-4 w-4" />
                {submitting ? "Sending…" : "Send feedback"}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
